import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';

const PREFILL_KEYS = ['address', 'address2', 'city', 'state', 'zip', 'phone', 'deliveryNotes'];

/**
 * Fills a signed-in customer's saved address into the checkout form — only
 * into fields that are still blank, so a half-typed or deliberately-changed
 * form is never clobbered. Guest shoppers (not signed in) see no change.
 *
 * `delivery`/`setDelivery` come from FlowContext (see useFlow()). This lives
 * in its own hook rather than inside FlowProvider because the provider wraps
 * every storefront page (a fetch there would fire on /store and /menu too),
 * and it sits above AuthActionsProvider in the component tree.
 */
export function useDeliveryPrefill(delivery, setDelivery) {
  const { isAuthenticated, isLoading } = useAuth0();
  const ranRef = useRef(false);

  useEffect(() => {
    // delivery is intentionally not a dependency — reading it fresh on every
    // render would re-run this whenever the user types, and setDelivery(patch)
    // below would then fight typing instead of running once on arrival.
    if (isLoading || !isAuthenticated || ranRef.current) return;
    ranRef.current = true;

    let cancelled = false;
    api.get('/account/address', { timeout: 10000 })
      .then(({ data }) => {
        if (cancelled || !data?.success || !data.address) return;
        const patch = {};
        PREFILL_KEYS.forEach((key) => {
          const current = delivery[key];
          const incoming = data.address[key === 'address' ? 'line1' : key];
          if (!String(current || '').trim() && incoming) patch[key] = incoming;
        });
        if (Object.keys(patch).length) setDelivery(patch);
      })
      // A failed prefill must leave a normal, empty, fully usable form — this
      // is a convenience, not something checkout depends on.
      .catch(() => {});

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);
}
